export interface GlossaryTerm {
  term: string;
  definition: string;
  related: string[]; // pattern slugs
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    term: 'AI Agent',
    definition:
      'A software system that perceives its environment and takes autonomous actions to achieve a goal on behalf of a user. Unlike a chatbot, an agent can execute multi-step tasks, use tools, and make decisions without step-by-step human instruction.',
    related: ['human-in-the-loop-checkpoint', 'agent-scope-definition', 'autonomous-action-log'],
  },
  {
    term: 'Agentic UI',
    definition:
      'An interface where an AI agent takes actions autonomously rather than responding to direct user commands. The user sets a goal; the agent pursues it.',
    related: ['autonomous-action-log', 'agent-scope-definition', 'undo-agent-actions'],
  },
  {
    term: 'Context Window',
    definition:
      'The amount of text a language model can process at one time. When a conversation exceeds the context window, the model loses access to earlier parts - affecting responses and memory.',
    related: ['no-screen-orientation-voice', 'clarification-without-interrogation'],
  },
  {
    term: 'Deterministic vs Probabilistic',
    definition:
      'Traditional software is deterministic - same input, same output. AI systems are probabilistic - same input may produce different outputs. This fundamentally changes how designers plan for edge cases.',
    related: ['graceful-agent-failure', 'decision-explanation-on-demand', 'expectation-setting-for-ai-output'],
  },
  {
    term: 'Graceful Degradation',
    definition:
      'A design principle where a system maintains basic functionality when part of it fails. In AX, an agent that can\'t complete a task should provide useful partial results or clear next steps.',
    related: ['graceful-agent-failure', 'graceful-topic-boundaries', 'clarification-without-interrogation'],
  },
  {
    term: 'Hallucination',
    definition:
      'When an AI model generates information that sounds plausible but is factually incorrect or fabricated. A key design challenge for interfaces displaying AI-generated content as authoritative.',
    related: ['decision-explanation-on-demand', 'expectation-setting-for-ai-output', 'source-attribution'],
  },
  {
    term: 'Human-in-the-Loop',
    definition:
      'A design approach where a human reviews or approves agent actions at key points in an automated workflow, particularly before irreversible or high-stakes actions.',
    related: ['human-in-the-loop-checkpoint', 'undo-agent-actions', 'permission-scoping'],
  },
  {
    term: 'Latency',
    definition:
      'The delay between a user request and the system response. In AI interfaces, latency is longer and more variable than traditional software - making it a key design consideration.',
    related: ['plan-preview-and-live-progress', 'designing-for-silence-voice-ui'],
  },
  {
    term: 'Prompt',
    definition:
      'The input text given to a language model to guide its response. Includes both the user\'s message and system-level instructions shaping agent behaviour and constraints.',
    related: ['expectation-setting-for-ai-output', 'graceful-topic-boundaries'],
  },
  {
    term: 'RAG (Retrieval-Augmented Generation)',
    definition:
      'A technique that enhances AI responses by retrieving relevant information from a knowledge base before generating a reply. Allows agents to work with domain-specific information beyond their training data.',
    related: ['decision-explanation-on-demand', 'source-attribution'],
  },
  {
    term: 'Tool Calling',
    definition:
      'The ability of an AI model to invoke external tools or APIs - like searching the web, running code, or sending an email. What transforms a chatbot into an agent capable of real-world actions.',
    related: ['human-in-the-loop-checkpoint', 'autonomous-action-log', 'graceful-agent-failure'],
  },
  {
    term: 'Voice UI',
    definition:
      'An interface where the primary interaction is spoken language. No screen, no clicks - entire experience is conversation. Design challenges include orientation, error recovery, and managing silence.',
    related: ['designing-for-silence-voice-ui', 'no-screen-orientation-voice', 'clarification-without-interrogation'],
  },
].sort((a, b) => a.term.localeCompare(b.term));
